/**
 * Manuals / handbook / standards attached to a checklist set (Settings master).
 * Publish/approve of the checklist itself already auto-applies via resolveEffective.
 */
import { useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Upload, Trash2, Download, BookOpen } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';

const KIND_OPTIONS = [
  { value: 'HANDBOOK', label: 'Handbook (e.g. MBIE CS Handbook)' },
  { value: 'MANUAL', label: 'Equipment manual' },
  { value: 'STANDARD', label: 'Standard / AS/NZS' },
  { value: 'OTHER', label: 'Other reference' },
];

export default function ChecklistSetDocuments({ ssCode, scope = 'SS_DEFAULT', unitType = '' }) {
  const queryClient = useQueryClient();
  const fileRef = useRef(null);
  const kindRef = useRef(null);
  const qKey = ['checklist-set-docs', ssCode, scope, unitType || ''];

  const { data, isLoading } = useQuery({
    queryKey: qKey,
    queryFn: async () => {
      const res = await api.get('/checklist-sets/documents', {
        params: {
          ssCode,
          scope,
          unitType: scope === 'UNIT_TYPE' ? unitType : undefined,
        },
      });
      return res.data;
    },
    enabled: !!ssCode && (scope !== 'UNIT_TYPE' || !!unitType),
  });

  const docs = data?.data || [];

  const uploadMutation = useMutation({
    mutationFn: async ({ file, docKind }) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('ssCode', ssCode);
      fd.append('scope', scope);
      if (scope === 'UNIT_TYPE') fd.append('unitType', unitType);
      fd.append('docKind', docKind || 'MANUAL');
      const res = await api.post('/checklist-sets/documents', fd, { timeout: 0 });
      return res.data;
    },
    onSuccess: () => {
      toast.success('Reference document attached');
      queryClient.invalidateQueries({ queryKey: qKey });
      if (fileRef.current) fileRef.current.value = '';
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Upload failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/checklist-sets/documents/${id}`),
    onSuccess: () => {
      toast.success('Document removed');
      queryClient.invalidateQueries({ queryKey: qKey });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Delete failed'),
  });

  const download = async (id, name) => {
    try {
      const res = await api.get(`/checklist-sets/documents/${id}/download`, {
        responseType: 'blob',
        timeout: 0,
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = name || 'document.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Download failed');
    }
  };

  return (
    <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-start gap-2 mb-2">
        <BookOpen className="w-5 h-5 text-slate-600 shrink-0 mt-0.5" />
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Manuals &amp; reference documents</h3>
          <p className="text-xs text-slate-600 mt-0.5">
            Attach the Compliance Schedule Handbook, manufacturer manuals, or standards used to write this
            checklist. When you <strong>Publish / Approve</strong> the questions above, buildings pick up the new
            version automatically on the next inspection (no per-building copy).
          </p>
        </div>
      </div>

      {isLoading ? (
        <p className="text-xs text-gray-500">Loading documents…</p>
      ) : docs.length === 0 ? (
        <p className="text-xs text-gray-500 mb-3">No manuals attached yet.</p>
      ) : (
        <ul className="mb-3 space-y-2">
          {docs.map((d) => (
            <li
              key={d.id}
              className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm"
            >
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-4 h-4 text-red-600 shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 truncate">{d.original_name}</p>
                  <p className="text-[11px] text-gray-500">
                    {d.doc_kind}
                    {d.size_bytes != null ? ` · ${(d.size_bytes / 1024).toFixed(0)} KB` : ''}
                  </p>
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button
                  type="button"
                  className="p-1.5 text-gray-600 hover:text-primary-700"
                  title="Download"
                  onClick={() => download(d.id, d.original_name)}
                >
                  <Download className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  className="p-1.5 text-gray-600 hover:text-red-600"
                  title="Remove"
                  onClick={() => {
                    if (window.confirm(`Remove “${d.original_name}”?`)) deleteMutation.mutate(d.id);
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="block text-[11px] text-gray-600 mb-1">Type</label>
          <select
            ref={kindRef}
            defaultValue="HANDBOOK"
            className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white"
          >
            {KIND_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <label className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-600 text-white rounded-lg text-sm cursor-pointer hover:bg-primary-700">
          {uploadMutation.isPending ? (
            'Uploading…'
          ) : (
            <>
              <Upload className="w-4 h-4" />
              Upload PDF / file
            </>
          )}
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,application/pdf"
            className="hidden"
            disabled={uploadMutation.isPending}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              uploadMutation.mutate({
                file,
                docKind: kindRef.current?.value || 'MANUAL',
              });
            }}
          />
        </label>
      </div>
    </div>
  );
}
